import { HistoryBasedValueRequest } from '@ltvco/refresh-lib/api';
import {
  HiddenSectionsProvider,
  LLMProvider,
  usePermissions,
  useReportCountryContext,
  useReportContext,
  useAppConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { usePublicReceipts } from '@ltvco/refresh-lib/hooks';
import { Grid, Box } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
  useScreenSize,
} from '@ltvco/refresh-lib/utils';
import {
  ReportLoading,
  ReportNullState,
  ReportRating,
  UpgradePremiumDataModal,
  VehicleReport,
  VehicleReportDisclaimer,
  AdditionalRecordsModal,
  EtagCTA,
  HideableSection,
  NotesSection,
  generateVehicleSpecs,
  getPremiumRecordsFound,
  useAccount,
  useFreeUpgradeMutation,
  ReportChangesOverview,
  enqueueSnackbar,
  useVehicleValuation,
  updateSetting,
} from '@ltvco/refresh-lib/v1';
import {
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportActionsWithDateUpdated,
  ReportNavigationMenuProps,
  VehicleDetailedHistorySection,
  VehicleTitleSummarySection,
  VehicleDamageHistorySection,
  VehicleSmartInsightsSection,
  VehicleReportOverview,
  VehicleSalesSection,
  VehicleDamageHistoryV2Section,
  UserReportPreferencesModal,
  VehicleSpecsSection,
  VehicleMaintenanceScheduleSection,
  VehicleOwnershipCostSection,
  VehicleAuctionsSection,
  VehicleRecallsSection,
  VehicleSafetyRatingsSection,
  VehicleServiceRepairsSection,
  VehicleTheftsSection,
  VehicleWarrantySection,
  VehicleOwnershipHistorySection,
  VehicleExportsSection,
  VehicleInspectionsSection,
  VehicleSafetyComplaintsSection,
  GetMostOutSection,
  VehicleTrimSelect,
  HideableSectionWithPermission,
  ReportNavigationMenuWithPermissions,
  TeaserReportUpgradeModal,
  ReportResponse,
  PartialVINModal,
  CommunityBuzz,
  SocialSentiment,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn, UseQueryResult } from '@ltvco/refresh-lib/vendors';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AddeptIoContainer } from 'components/AddeptIo/AddeptIo';
import { DamageAnalyzerCTA } from 'components/DamageAnalyzerCTA';
import { RepairEstimateCTA } from 'components/RepairEstimateCTA';
import { ReportNavigationSelfInspectionCTA } from 'components/SelfInspection/CTAs/ReportNavigationSelfInspectionCTA/ReportNavigationSelfInspectionCTA';
import { ReportSectionSelfInspectionCTA } from 'components/SelfInspection/CTAs/ReportSectionSelfInspectionCTA/ReportSectionSelfInspectionCTA';
import { SelfInspectionResults } from 'components/SelfInspection/SelfInspectionResults/SelfInspectionResults';
import { ValueForecasterCTA } from 'components/ValueForecasterCTA';
import { useAgentChatVehicle } from 'context/AgentChatVehicleContext';
import { useBowtie } from 'hooks/useBowtie';
import { useRerunReportAndRefresh } from 'hooks/useRerunReportAndRefresh';
import { useStatesContact } from 'hooks/useStatesContact';
import { useVehicleComplaints } from 'hooks/useVehicleComplaints';
import {
  getVehicleNavLinkData,
  getVehicleNavLinkDataV2,
} from 'navLinkData/vehicleNavLinkData';

import { VehicleValueSection } from '../VehicleValueSection/VehicleValueSection';

import { getDefaultUserReportPreferences } from './Preferences/defaultUserReportPreferences';
import { TeaserVehicleReport } from './TeaserVehicleReport';
import { VehicleReviews } from './VehicleReviews/VehicleReviews';

import type { ReportRouteProps } from './types';

interface VehicleReportProps extends ReportRouteProps {
  preloadedData?: ReportResponse;
  onInvalidVin?: (vin: string) => void;
}

const TEASER_REPORT_FLAG = 'accordion';

export function VehicleReportPage({
  permalink,
  preloadedData,
  onInvalidVin,
}: VehicleReportProps) {
  const { handleSetVehicleReportData, reportCountry } =
    useReportCountryContext();
  const isCanadian = reportCountry === 'CA';
  const contextReportQuery = useReportContext()?.reportQuery;
  const reportIsMonitored = contextReportQuery?.reportIsMonitored ?? false;

  // Use preloaded data if available (from ChatAI), otherwise use the context query
  const reportQuery = preloadedData
    ? ({
        data: preloadedData,
        isInitialLoading: false,
        isLoading: false,
        isError: false,
        error: null,
        isSuccess: true,
        status: 'success' as const,
      } as UseQueryResult<ReportResponse, Error>)
    : contextReportQuery;
  const showOnlyReport = preloadedData;
  const { data, isInitialLoading, isError } = reportQuery ?? {};
  const { shouldPromptTeaserReportUpgrade } = usePermissions();
  const navigate = useNavigate();
  // AB Tests
  const isInDamageHistory = useFeatureIsOn('bump-4464');
  // Hooks
  const { isMobile } = useScreenSize();
  const { data: accountData } = useAccount(true);
  const { setVehicle: setAgentChatVehicle } = useAgentChatVehicle();
  const [preferencesModalVisible, setPreferencesModalVisible] = useState(false);
  const hiddenSections =
    accountData?.account?.user_report_preferences.hidden_sections
      .vehicle_report || [];
  const reportSource = data as any;
  const newReport =
    data?.meta && new VehicleReport(reportSource, reportCountry);
  const reportData = newReport?.data;
  const currentVehicle = reportData?.automobiles?.[0];
  const searchData = reportData?.meta?.search_data;
  const year =
    currentVehicle?.year?.toString() || searchData?.year?.toString() || '';
  const make = currentVehicle?.make || searchData?.make || '';
  const model = currentVehicle?.model || searchData?.model || '';
  const trim =
    currentVehicle?.design?.trim ||
    currentVehicle?.trim ||
    searchData?.trim ||
    'Base';
  const reportTitle =
    reportData?.automobiles?.[0]?.vehicleYMM?.toUpperCase() ||
    reportData?.meta?.metaName ||
    '';
  const meta = reportData?.meta || undefined;
  const [isTeaserReport, setIsTeaserReport] = useState(
    meta?.report_info?.plan === TEASER_REPORT_FLAG,
  );
  const [showTeaserReportUpgradeModal, setShowTeaserReportUpgradeModal] =
    useState(shouldPromptTeaserReportUpgrade(meta));
  const [teaserModalDismissed, setTeaserModalDismissed] = useState(false);
  const [showPartialVINModal, setShowPartialVINModal] = useState(false);

  const defaultUserReportPreferences = getDefaultUserReportPreferences();

  const adjustedInitialVehicleReportPreferences = isInDamageHistory
    ? [
        ...defaultUserReportPreferences.filter(
          (item) => item.navSection !== 'Damage History',
        ),
        {
          navSection: 'Damage History',
          sections: [
            {
              nameToDisplay: 'Accidents and Damages',
              nameToRecord: 'accidents_and_damages',
              checked: false,
            },
            {
              nameToDisplay: 'Repair Quotes',
              nameToRecord: 'repair_quotes',
              checked: false,
            },
            {
              nameToDisplay: 'Salvage Records',
              nameToRecord: 'salvage_records',
              checked: false,
            },
          ],
        },
      ]
    : defaultUserReportPreferences;

  const [sectionsToHide, setSectionsToHide] = useState(
    adjustedInitialVehicleReportPreferences,
  );
  const vehicleComplaints = useVehicleComplaints(year, make, model);
  const upgradeReport = useFreeUpgradeMutation(permalink);
  const rerunReportAndRefresh = useRerunReportAndRefresh();
  const statesContact = useStatesContact();

  const [showAdditionalRecordsModal, setShowAdditionalRecordsModal] =
    useState(false);
  const [premiumRecordsFound, setPremiumRecordsFound] = useState<string[]>([]);
  const [upgradeReportCalled, setUpgradeReportCalled] = useState(false);
  const bowtie = useBowtie(data?.meta?.search_data?.vin);
  const { logError } = useAppConfigContext();
  const {
    publicReceipts,
    isPublicReceiptsLoading,
    hasMoreReceipts,
    isFetchingNextReceipts,
    fetchNextReceiptsPage,
  } = usePublicReceipts({
    vin: reportData?.meta?.search_data?.vin,
    licensePlate: reportData?.meta?.search_data?.license_plate,
    state: reportData?.meta?.search_data?.state,
  });

  const hasSkippedCommunityBuzzModal =
    accountData?.account?.user_settings?.hide_community_buzz_modal;

  const handleCommunityBuzzModalShown = useCallback(async () => {
    if (hasSkippedCommunityBuzzModal) return;

    try {
      await updateSetting({
        setting: 'hide_community_buzz_modal',
        state: true,
      });
    } catch (error) {
      logError('Error updating user setting', error as Error);
    }
  }, [hasSkippedCommunityBuzzModal, logError]);

  const { hash } = useLocation();
  useScrollToSectionOnNavigate(hash, reportQuery as UseQueryResult<any, Error>);

  const valuationRequestData = {
    year: currentVehicle?.year?.toString() ?? '',
    make: currentVehicle?.make ?? '',
    model: currentVehicle?.model ?? '',
    mileage: currentVehicle?.mileage?.miles ?? 0,
    location: (() => {
      const city = currentVehicle?.city ?? currentVehicle?.origin?.city ?? '';
      const state = currentVehicle?.state ?? '';
      const zip = currentVehicle?.zip ?? '';

      const parts = [city, state, zip].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : '';
    })(),
  };

  const isValidValuationRequest = (
    data: typeof valuationRequestData,
  ): boolean => {
    return (
      Boolean(data.year) &&
      Boolean(data.make) &&
      Boolean(data.model) &&
      data.mileage > 0
    );
  };

  const { data: fetchedValuation } = useVehicleValuation(valuationRequestData, {
    enabled: isValidValuationRequest(valuationRequestData),
  });

  useEffect(() => {
    document.title = `${reportTitle || 'Search'} - Bumper`;
    if (reportData) {
      handleSetVehicleReportData(reportData);
    }
  }, [data]);

  // Check for partial VIN and show modal if needed
  useEffect(() => {
    const vin = reportData?.meta?.search_data?.vin;
    const isPartialVIN = vin && vin.length === 14 && Number(year) >= 1981;

    if (isPartialVIN) {
      setShowPartialVINModal(true);
    }
  }, [reportData?.meta?.search_data?.vin, year]);

  // Feed the report's vehicle into the agent chat so the drawer shows the
  // vetter intents while this report is open; cleared on unmount.
  useEffect(() => {
    const vin = reportData?.meta?.search_data?.vin;
    const ymmt = [year, make, model].filter(Boolean).join(' ');
    const vehicleIdentifier = vin || ymmt;

    if (!vehicleIdentifier) return;

    setAgentChatVehicle({
      vehicleIdentifier,
      vehicleTitle: ymmt || vehicleIdentifier,
    });
    return () => setAgentChatVehicle(null);
  }, [reportData?.meta?.search_data?.vin, year, make, model]);

  useEffect(() => {
    setIsTeaserReport(meta?.report_info?.plan === TEASER_REPORT_FLAG);
  }, [meta?.report_info?.plan]);

  useEffect(() => {
    if (!teaserModalDismissed) {
      setShowTeaserReportUpgradeModal(shouldPromptTeaserReportUpgrade(meta));
    }
  }, [meta, teaserModalDismissed, shouldPromptTeaserReportUpgrade]);

  useEffect(() => {
    if (hiddenSections) {
      setSectionsToHide((prevSections) =>
        prevSections.map((navSection) => ({
          ...navSection,
          sections: navSection.sections.map((section) => ({
            ...section,
            checked: !hiddenSections.includes(section.nameToRecord),
          })),
        })),
      );
    }
  }, [accountData]);

  const vehicleNavLinkDataV2 = getVehicleNavLinkDataV2(
    reportData || null,
    vehicleComplaints?.data?.complaints?.length || 0,
    bowtie?.data?.answer_sets?.length || 0,
    publicReceipts?.length || 0,
  );

  const vehicleNavLinkData = getVehicleNavLinkData(
    reportData || null,
    vehicleComplaints?.data?.complaints?.length || 0,
    bowtie?.data?.answer_sets?.length || 0,
  );

  const handleRedirectToUpgrade = useCallback(() => {
    navigate('/subscribe');
  }, [navigate]);

  const handleUpgrade = useCallback(async () => {
    try {
      await rerunReportAndRefresh(reportData?.meta);
    } catch {
      enqueueSnackbar('Error rerunning report. Please try again', {
        variant: 'error',
      });
    }
  }, [rerunReportAndRefresh, reportData?.meta]);

  const handleClosePartialVINModal = useCallback(() => {
    setShowPartialVINModal(false);
  }, []);

  useEffect(() => {
    const searchedVin = reportData?.meta?.search_data?.vin;
    const isZeroedReport = isZeroed(reportQuery as UseQueryResult<any, Error>);
    const isEmpty = reportData?.automobiles?.length === 0;
    if ((isZeroedReport || isEmpty) && searchedVin) {
      onInvalidVin?.(searchedVin);
    }
  }, [reportData, reportQuery, onInvalidVin]);

  if (isInitialLoading || isError) {
    return <ReportLoading menuItems={10} />;
  }

  const zeroed = isZeroed(reportQuery as UseQueryResult<any, Error>);
  if (zeroed) {
    return <ReportNullState />;
  }

  if (!reportData && (upgradeReport.isSuccess || upgradeReport.isLoading)) {
    return <UpgradePremiumDataModal />;
  }

  if (!reportData || !reportData.automobiles)
    return <ReportLoading menuItems={10} />;

  if (reportData?.automobiles?.length === 0) return <ReportNullState />;

  const vehicleSpecs = generateVehicleSpecs(reportData);

  const isYMMSearch = reportData.meta?.search_by === 'ymm';
  const ymmPresent = [year, make, model].filter(Boolean);
  const hasAllYMMPresent = ymmPresent.length === 3;
  const ymm = ymmPresent.join(' ');
  const reportSubtitle = !isYMMSearch ? reportData.meta?.metaName : '';

  const handleFreeUpgrade = () => {
    upgradeReport.mutate({ permalink });
    setUpgradeReportCalled(true);
  };

  const handleOnClickPremiumData = () => {
    setShowAdditionalRecordsModal(!showAdditionalRecordsModal);
    setUpgradeReportCalled(!upgradeReportCalled);
  };

  const handlePreferencesModalVisibility = () => {
    setPreferencesModalVisible(!preferencesModalVisible);
  };

  if (
    reportData &&
    upgradeReportCalled &&
    upgradeReport.isSuccess &&
    !showAdditionalRecordsModal
  ) {
    setPremiumRecordsFound(getPremiumRecordsFound(reportData));
    setShowAdditionalRecordsModal(true);
  }

  const date = new DateUtil();
  const reportUpdateDate = reportData.meta?.updated_at
    ? date.parseDateFromString(
        reportData.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ',
      )
    : '';

  const damageHistoryProps = {
    vehicleAccidentsList: reportData?.automobiles?.[0]?.accidents,
    vehicleRecallsList: reportData?.automobiles?.[0]?.recalls,
    vehicleServiceRepairsList: reportData?.automobiles?.[0]?.service_repairs,
    vehicleTheftsList: reportData?.automobiles?.[0]?.thefts,
    vehicleLiensList: reportData?.automobiles?.[0]?.liens,
    vehicleImpoundsList: reportData?.automobiles?.[0]?.impounds,
    vehicleTitleBrandsList: reportData?.automobiles?.[0]?.title_checks,
    vehicleTitleRecordsList: reportData?.automobiles?.[0]?.title_registrations,
    vehicleAuctionsDamageList:
      reportData?.automobiles?.[0]?.damage_history?.reported_damage,
    vehicleSalvagesList: reportData?.automobiles?.[0]?.salvage,
    vehicleRepairQuotesList:
      reportData?.automobiles?.[0]?.damage_history?.repair_quotes,
    nmvtisSuccess: reportData?.automobiles?.[0]?.nmvtis_success || false,
    reportUpgraded: reportData.report_upgraded,
    handleFreeUpgrade: handleFreeUpgrade,
  };

  const historyBasedValueParams: HistoryBasedValueRequest = {
    year: currentVehicle?.year?.toString() ?? '',
    make: reportData?.automobiles?.[0]?.make ?? '',
    model: currentVehicle?.model ?? '',
    accidents: currentVehicle?.accidentsCount ?? 0,
    damages: currentVehicle?.damageHistoryCount ?? 0,
    owners: currentVehicle?.ownershipHistoryCount ?? 0,
    recalls: currentVehicle?.recallsCount ?? 0,
    salvages: Boolean(currentVehicle?.salvagesCount),
    thefts: currentVehicle?.theftsCount ?? 0,
    title_brands: currentVehicle?.titleChecksCount ?? 0,
    market_value: fetchedValuation?.clean_retail?.adjusted ?? 0,
  };

  return (
    <HiddenSectionsProvider reportType="vehicle_report">
      <LLMProvider
        llmType="vehicle"
        llmData={currentVehicle}
        historyBasedValueParams={historyBasedValueParams}
      >
        {upgradeReport.isLoading && <UpgradePremiumDataModal />}
        <AdditionalRecordsModal
          recordsFound={premiumRecordsFound}
          showModal={showAdditionalRecordsModal}
          handleOnClose={handleOnClickPremiumData}
        />
        <Grid container direction={'row'} columns={12} spacing={7}>
          <PartialVINModal
            isOpen={showPartialVINModal}
            onClose={handleClosePartialVINModal}
          />
          {!showOnlyReport && (
            <Grid
              size={{ sm: 12, md: 4, lg: 4 }}
              sx={{
                display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' },
              }}
            >
              {!isTeaserReport ? (
                <ReportNavigationMenu
                  reportType="Vehicle Report"
                  headerTitle={reportTitle || reportSubtitle || ''}
                  subheaderTitle={reportSubtitle || ''}
                  showReportType={false}
                  menuItems={
                    vehicleNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
                  }
                />
              ) : (
                <ReportNavigationMenuWithPermissions
                  reportType="Vehicle Report"
                  headerTitle={reportTitle || reportSubtitle || ''}
                  subheaderTitle={reportSubtitle || ''}
                  showReportType={false}
                  menuItems={
                    vehicleNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
                  }
                  customTitleStyle={{
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    color: '#0A0B0D',
                  }}
                  customCTA={
                    <ReportNavigationSelfInspectionCTA
                      vin={reportData?.meta?.search_data?.vin}
                      permalink={permalink}
                    />
                  }
                />
              )}
            </Grid>
          )}
          <Grid
            size={{
              sm: 12,
              md: showOnlyReport ? 12 : 8,
              lg: showOnlyReport ? 12 : 8,
            }}
            marginTop={{
              xs: 0,
              md: 0.5,
            }}
            sx={{
              '&.MuiGrid-item': {
                paddingTop: { xs: 3.75, md: 7 },
              },
            }}
          >
            {reportIsMonitored && (
              <ReportChangesOverview
                permalink={permalink}
                navLinkData={vehicleNavLinkData}
              />
            )}
            {!showOnlyReport && (
              <>
                <Box
                  sx={{
                    height: 30,
                    backgroundColor: '#f8f8f8',
                    position: 'sticky',
                    marginBottom: '-10px',
                    marginX: -1,
                    top: 'var(--header-height, 55px)',
                    zIndex: 19,
                    display: {
                      xs: 'none',
                      sm: 'block',
                      md: 'block',
                      lg: 'block',
                    },
                  }}
                />
                <ReportActionsWithDateUpdated
                  reportTitle="Vehicle"
                  reportType="vehicle"
                  dateUpdated={reportUpdateDate}
                  reportUpgraded={false}
                  sx={{
                    top: 'var(--header-height, 55px)',
                    position: 'sticky',
                    zIndex: 19,
                  }}
                />
              </>
            )}
            {isYMMSearch && (
              <VehicleTrimSelect year={year} make={make} model={model} />
            )}
            <VehicleReportOverview
              reportType="vehicle-report"
              vehicleReportData={reportData}
              permalink={permalink}
              nmvtisSuccess={reportData?.automobiles?.[0]?.nmvtis_success}
              reportUpgraded={reportData.report_upgraded}
              handleFreeUpgrade={handleFreeUpgrade}
              isFreePlan={isTeaserReport}
              redirectToUpgrade={handleRedirectToUpgrade}
            />
            {!isYMMSearch && hasAllYMMPresent && (
              <HideableSectionWithPermission
                nameToDisplay="Smart Insights"
                nameToRecord="smart_insights"
                id="smart-insights"
                isTeaserReport={isTeaserReport}
                reportData={reportData}
              >
                <Box mb={2.5} id="smart-insights">
                  <VehicleSmartInsightsSection reportData={reportData} />
                </Box>
              </HideableSectionWithPermission>
            )}
            <GetMostOutSection
              sectionId="get-most-out"
              claimed={reportData.meta?.tag === 'self' ? true : false}
              meta={reportData.meta}
            />
            <AddeptIoContainer />
            {/* Teaser Version - Only 4 main sections */}
            {isTeaserReport ? (
              <TeaserVehicleReport
                vehicleSpecs={vehicleSpecs}
                reportData={reportData}
                isTeaserReport={isTeaserReport}
              />
            ) : (
              <>
                <VehicleReviews
                  ymmText={ymm}
                  ymm={{ year, make, model }}
                  permalink={permalink}
                />
                <>
                  {!isYMMSearch && (
                    <>
                      <HideableSection
                        nameToDisplay="Ownership History"
                        nameToRecord="ownership_history"
                        id="vehicle-ownership-history"
                      >
                        <VehicleOwnershipHistorySection
                          vehicleOwnershipHistories={
                            reportData?.automobiles?.[0]?.ownership_history
                          }
                        />
                      </HideableSection>
                      <>
                        <HideableSection
                          nameToDisplay="Title Summary"
                          nameToRecord="title_summary"
                          id="vehicle-title-summary"
                        >
                          <VehicleTitleSummarySection
                            vehicleTitleRegistrationsList={
                              reportData?.automobiles?.[0]?.title_registrations
                            }
                            vehicleTitleBrandsList={
                              reportData?.automobiles?.[0]?.title_checks
                            }
                            vehicleLiensList={
                              reportData?.automobiles?.[0]?.liens
                            }
                            vehicleImpoundList={
                              reportData?.automobiles?.[0]?.impounds
                            }
                            statesContact={
                              statesContact?.data?.states_contact || []
                            }
                            nmvtisSuccess={
                              reportData?.automobiles?.[0]?.nmvtis_success ||
                              false
                            }
                            reportUpgraded={reportData.report_upgraded}
                            handleFreeUpgrade={handleFreeUpgrade}
                          />
                        </HideableSection>
                        {!isCanadian && <EtagCTA />}
                      </>
                      <HideableSection
                        nameToDisplay="Export Records"
                        nameToRecord="export_records"
                        id="vehicle-exports"
                      >
                        <VehicleExportsSection
                          vehicleExportList={
                            reportData?.automobiles?.[0]?.exports
                          }
                        />
                      </HideableSection>
                      <HideableSection
                        nameToDisplay="Theft Records"
                        nameToRecord="theft_records"
                        id="vehicle-thefts"
                      >
                        <VehicleTheftsSection
                          vehicleTheftsList={
                            reportData?.automobiles?.[0]?.thefts
                          }
                        />
                      </HideableSection>
                      <HideableSection
                        nameToDisplay="Inspections"
                        nameToRecord="inspections"
                        id="vehicle-inspections"
                      >
                        <VehicleInspectionsSection
                          inspectionEvents={
                            reportData?.automobiles?.[0]?.inspection_events
                          }
                        />
                      </HideableSection>
                    </>
                  )}
                </>
                {!isYMMSearch && (
                  <>
                    <HideableSection
                      nameToDisplay="Sale Listings"
                      nameToRecord="sales"
                      id="vehicle-sales"
                    >
                      <VehicleSalesSection
                        vehicleSalesList={reportData?.automobiles?.[0]?.sales}
                      />
                    </HideableSection>
                    <HideableSection
                      nameToDisplay="Auctions"
                      nameToRecord="auctions"
                      id="vehicle-auctions"
                    >
                      <VehicleAuctionsSection
                        vehicleAuctionsList={
                          reportData?.automobiles?.[0]?.auctions
                        }
                        vehicleYMM={
                          reportData?.automobiles?.[0]?.vehicleYMM || ''
                        }
                      />
                    </HideableSection>
                    {reportData?.automobiles?.[0]?.detailedHistory && (
                      <HideableSection
                        nameToDisplay="Detailed History"
                        nameToRecord="detailed_history"
                        id="detailed-history"
                      >
                        <VehicleDetailedHistorySection
                          detailedHistory={
                            reportData?.automobiles?.[0]?.detailedHistory
                          }
                          ymm={ymm}
                        />
                      </HideableSection>
                    )}
                    <>
                      <HideableSection
                        nameToDisplay="Damage History"
                        nameToRecord="damage_history"
                        id="vehicle-damage-history"
                      >
                        {isInDamageHistory ? (
                          <VehicleDamageHistoryV2Section
                            {...damageHistoryProps}
                          />
                        ) : (
                          <VehicleDamageHistorySection
                            {...damageHistoryProps}
                          />
                        )}
                      </HideableSection>
                      <DamageAnalyzerCTA />
                    </>
                    <HideableSection
                      nameToDisplay="Service and Repairs"
                      nameToRecord="service_and_repairs"
                      id="vehicle-service-and-repairs"
                    >
                      <VehicleServiceRepairsSection
                        repairs={reportData?.automobiles?.[0]?.repairs || []}
                        publicReceipts={publicReceipts}
                        isPublicReceiptsLoading={isPublicReceiptsLoading}
                        hasMoreReceipts={hasMoreReceipts}
                        isFetchingNextReceipts={isFetchingNextReceipts}
                        fetchNextReceiptsPage={fetchNextReceiptsPage}
                      />
                    </HideableSection>
                    <RepairEstimateCTA />

                    <HideableSection
                      nameToDisplay="Maintenance Schedule"
                      nameToRecord="maintenance_schedule"
                      id="vehicle-maintenance-schedule"
                    >
                      <VehicleMaintenanceScheduleSection
                        vehicleMaintenanceSchedules={
                          reportData?.automobiles?.[0]?.maintenances || []
                        }
                      />
                    </HideableSection>
                  </>
                )}
                <HideableSection
                  nameToDisplay="Recalls"
                  nameToRecord="recalls"
                  id="vehicle-recalls"
                >
                  <VehicleRecallsSection
                    vehicleRecallList={[
                      ...(reportData?.automobiles?.[0]?.openRecalls || []),
                      ...(isYMMSearch
                        ? reportData?.automobiles?.[0]?.otherRecalls || []
                        : reportData?.automobiles?.[0]?.closedRecalls || []),
                    ]}
                  />
                </HideableSection>
                <HideableSection
                  nameToDisplay="Self Inspection"
                  nameToRecord="self_inspection"
                  id="self-inspection"
                >
                  <SelfInspectionResults
                    vin={reportData?.meta?.search_data?.vin}
                    vehicleType={{
                      vehicleType: reportData?.automobiles?.[0]?.type,
                      vehicleSize: reportData?.automobiles?.[0]?.size,
                      vehicleDoors: reportData?.automobiles?.[0]?.design?.doors,
                    }}
                  />
                  <ReportSectionSelfInspectionCTA
                    vin={reportData?.meta?.search_data?.vin}
                    permalink={permalink}
                  />
                </HideableSection>
                <>
                  <HideableSection
                    nameToDisplay="Market Value"
                    nameToRecord="market_value"
                    id="vehicle-market"
                  >
                    <VehicleValueSection vehicleReport={reportData} />
                  </HideableSection>
                  <ValueForecasterCTA />
                  <HideableSection
                    nameToDisplay="Ownership Costs"
                    nameToRecord="ownership_costs"
                    id="vehicle-ownership-cost"
                  >
                    <VehicleOwnershipCostSection
                      vehicleOwnershipCosts={
                        reportData.automobiles[0]?.ownership_costs
                      }
                      vehicleYMM={reportData.automobiles[0]?.vehicleYMM || ''}
                    />
                  </HideableSection>
                </>
                <HideableSection
                  nameToDisplay="Specs"
                  nameToRecord="specifications"
                  id="vehicle-specs"
                >
                  <VehicleSpecsSection
                    vehicleSpecs={vehicleSpecs}
                    vehicleDesign={reportData?.automobiles?.[0]?.design}
                    firstVehicleImage={
                      reportData?.automobiles?.[0]?.images?.[0]
                    }
                  />
                </HideableSection>
                <HideableSection
                  nameToDisplay="Vehicle Safety"
                  nameToRecord="vehicle_safety"
                  id="vehicle-warranty"
                >
                  <VehicleSafetyRatingsSection
                    vehicleSafetyRatingsList={
                      reportData?.automobiles?.[0]?.safety_ratings || []
                    }
                    vehicleYMM={reportData?.automobiles?.[0]?.vehicleYMM || ''}
                    complaintsCount={
                      vehicleComplaints?.data?.complaints?.length || 0
                    }
                  />
                </HideableSection>
                <HideableSection
                  nameToDisplay="Warranties"
                  nameToRecord="warranties"
                  id="vehicle-warranty"
                >
                  <VehicleWarrantySection
                    vehicleWarrantyList={
                      reportData?.automobiles?.[0]?.warranties
                    }
                  />
                </HideableSection>

                <VehicleSafetyComplaintsSection
                  ymm={reportData?.automobiles?.[0]?.vehicleYMM || ''}
                  complaints={vehicleComplaints?.data?.complaints}
                />
              </>
            )}
            {hasAllYMMPresent && (
              <>
                <CommunityBuzz
                  make={make}
                  model={model}
                  year={year}
                  trim={trim}
                  limitIntroModalToSession={true}
                  shouldHideModal={hasSkippedCommunityBuzzModal ?? true}
                  onModalShown={handleCommunityBuzzModalShown}
                />
                <SocialSentiment
                  make={make}
                  model={model}
                  year={year}
                  trim={trim}
                />
              </>
            )}
            <NotesSection permalink={permalink} />
            <ReportRating
              permalink={permalink}
              rating={reportData.meta?.rating || null}
            />
            <VehicleReportDisclaimer />
          </Grid>
        </Grid>
        {isMobile && !showOnlyReport && (
          <ReportNavigationMobileMenu
            reportType="Vehicle Report"
            headerTitle={reportTitle || reportSubtitle || ''}
            subheaderTitle={reportSubtitle || ''}
            showReportType={false}
            menuItems={
              vehicleNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
            customTitleStyle={{
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              color: '#0A0B0D',
            }}
          />
        )}
        {preferencesModalVisible && (
          <UserReportPreferencesModal
            sectionsToHide={sectionsToHide}
            handleOnClose={handlePreferencesModalVisibility}
          />
        )}
        {isTeaserReport && !teaserModalDismissed && (
          <TeaserReportUpgradeModal
            isOpen={showTeaserReportUpgradeModal}
            onClose={() => {
              setShowTeaserReportUpgradeModal(false);
              setTeaserModalDismissed(true);
            }}
            reportMeta={meta || undefined}
            handleUpgrade={handleUpgrade}
          />
        )}
      </LLMProvider>
    </HiddenSectionsProvider>
  );
}
